import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Avatar,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Skeleton,
  Card,
  CardContent,
  Tab,
  Tabs,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Phone,
  Email,
  LocationOn,
  Cake,
  CalendarMonth,
  MedicalServices,
  Warning,
} from '@mui/icons-material';
import { patientService } from '../../services/patient.service';
import { Patient, Appointment } from '../../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function PatientDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    if (id) {
      loadPatient();
    }
  }, [id]);

  const loadPatient = async () => {
    try {
      setLoading(true);
      const data = await patientService.getById(id!);
      setPatient(data);
      
      // Load patient history
      try {
        const history = await patientService.getHistory(id!);
        setAppointments(history.appointments || []);
      } catch (error) {
        console.error('Error loading patient history:', error);
      }
    } catch (error) {
      console.error('Error loading patient:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const formatDateTime = (date: string) => {
    return new Date(date).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const calculateAge = (birthDate: string | undefined) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, 'primary' | 'success' | 'error' | 'warning' | 'default'> = {
      scheduled: 'primary',
      confirmed: 'success',
      completed: 'default',
      cancelled: 'error',
      no_show: 'warning',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      scheduled: 'Agendada',
      confirmed: 'Confirmada',
      completed: 'Concluída',
      cancelled: 'Cancelada',
      no_show: 'Não compareceu',
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (!patient) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          Paciente não encontrado
        </Typography>
        <Button onClick={() => navigate('/patients')} sx={{ mt: 2 }}>
          Voltar para lista
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/patients')}
        sx={{ mb: 2 }}
      >
        Voltar
      </Button>

      {/* Patient Header */}
      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, mb: 3 }}>
        <Box sx={{ p: 3, display: 'flex', alignItems: 'flex-start', gap: 3 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: 'primary.main',
              fontSize: '2rem',
            }}
          >
            {patient.name.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h4" fontWeight="bold">
                {patient.name}
              </Typography>
              <Button variant="outlined" size="small" startIcon={<Edit />}>
                Editar
              </Button>
            </Box>
            <Grid container spacing={2}>
              {patient.phone && (
                <Grid item>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Phone fontSize="small" color="action" />
                    <Typography variant="body2">{patient.phone}</Typography>
                  </Box>
                </Grid>
              )}
              {patient.email && (
                <Grid item>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Email fontSize="small" color="action" />
                    <Typography variant="body2">{patient.email}</Typography>
                  </Box>
                </Grid>
              )}
              {patient.address && (
                <Grid item>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography variant="body2">{patient.address}</Typography>
                  </Box>
                </Grid>
              )}
              {patient.birthDate && (
                <Grid item>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Cake fontSize="small" color="action" />
                    <Typography variant="body2">
                      {formatDate(patient.birthDate)} ({calculateAge(patient.birthDate)} anos)
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
          <Button
            variant="contained"
            startIcon={<CalendarMonth />}
            onClick={() => navigate('/calendar')}
          >
            Nova Consulta
          </Button>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: '1px solid #e0e0e0', px: 2 }}
        >
          <Tab label="Informações" />
          <Tab label="Histórico de Consultas" />
          <Tab label="Tratamentos" />
        </Tabs>

        {/* Info Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ px: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <MedicalServices color="primary" />
                      <Typography variant="h6" fontWeight="600">
                        Histórico Médico
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {patient.medicalHistory || 'Nenhum histórico médico registrado'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Warning color="warning" />
                      <Typography variant="h6" fontWeight="600">
                        Alergias
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {patient.allergies || 'Nenhuma alergia registrada'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      CPF
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {patient.cpf || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Data de Cadastro
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formatDate(patient.createdAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Última Atualização
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formatDate(patient.updatedAt)}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Appointments Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ px: 3 }}>
            {appointments.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  Nenhuma consulta registrada
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Data/Hora</TableCell>
                      <TableCell>Dentista</TableCell>
                      <TableCell>Tratamento</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Observações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {appointments.map((appointment) => (
                      <TableRow key={appointment.id} hover>
                        <TableCell>{formatDateTime(appointment.startTime)}</TableCell>
                        <TableCell>
                          {appointment.dentist?.user?.name || '-'}
                        </TableCell>
                        <TableCell>
                          {appointment.treatment?.name || '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={getStatusLabel(appointment.status)}
                            color={getStatusColor(appointment.status)}
                          />
                        </TableCell>
                        <TableCell>{appointment.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>

        {/* Treatments Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ px: 3, textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              Lista de tratamentos em andamento e concluídos
            </Typography>
          </Box>
        </TabPanel>
      </Paper>
    </Box>
  );
}
